import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  LANGUAGE_LABELS,
  LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
  languageFromPath,
  pathInLanguage,
  type SupportedLanguage,
} from '../i18n/i18n'

// Pill-style switcher with a click-to-open dropdown of all supported
// languages. Switching navigates to the same page in the new language
// (preserves path + search + hash) and persists the choice in
// localStorage so the user doesn't have to re-pick.
//
// Two visual variants:
//   variant="nav"    — for the top nav (default sized)
//   variant="footer" — for the footer (smaller, subtler)

const LS_KEY = 'nimvoy.preferredLanguage'

export function setPreferredLanguage(lang: SupportedLanguage) {
  try {
    localStorage.setItem(LS_KEY, lang)
  } catch {
    // ignore — Safari private mode, etc.
  }
}

export function getPreferredLanguage(): SupportedLanguage | null {
  try {
    const v = localStorage.getItem(LS_KEY)
    if (v === 'en' || v === 'zh') return v
  } catch {
    // ignore
  }
  return null
}

interface Props {
  variant?: 'nav' | 'footer'
}

export function LanguageSwitcher({ variant = 'nav' }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const current = languageFromPath(location.pathname)

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function pick(lang: SupportedLanguage) {
    setOpen(false)
    setPreferredLanguage(lang)
    if (lang !== current) {
      const newPath = pathInLanguage(location.pathname, lang)
      // Navigate only. LanguageSync owns the i18next transition because the
      // language chunk has to be fetched first — calling changeLanguage here
      // would complete synchronously (no i18next backend is registered),
      // leaving LanguageSync's `i18n.language !== lang` guard false and the
      // chunk never requested, i.e. a page that claims to be Chinese and
      // renders entirely in English.
      navigate({ pathname: newPath, search: location.search, hash: location.hash })
    }
  }

  return (
    <div ref={rootRef} className={`lang-switch lang-switch--${variant}`}>
      <button
        type="button"
        className="lang-switch__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={LANGUAGE_NAMES[current]}
      >
        <span className="lang-switch__globe" aria-hidden>🌐</span>
        <span>{LANGUAGE_LABELS[current]}</span>
        <span className="lang-switch__caret" aria-hidden>▾</span>
      </button>
      {open && (
        <div className="lang-switch__menu" role="listbox">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang}
              type="button"
              role="option"
              aria-selected={lang === current}
              className={`lang-switch__item ${lang === current ? 'active' : ''}`}
              onClick={() => pick(lang)}
            >
              <span>{LANGUAGE_NAMES[lang]}</span>
              {lang === current && <span className="lang-switch__check" aria-hidden>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
